import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Filter, ChevronDown, X, Search, Check, Loader2 } from 'lucide-react';
import { useApi } from '../../utils/useApi';
import NumberInput from '../ui/NumberInput';

interface AudienceSelectorProps {
  tenantId: string;
  onAudienceSelect: (audience: any) => void;
  onRecipientCount: (count: number, excludedCount: number) => void;
}

interface User {
  id: string;
  name: string;
  phoneNo: string;
  email?: string;
}

interface FilterOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  defaultParams: Record<string, any>;
  paramLabel?: string; // label for the N-days input; filters without it need no parameters
}

const FILTER_OPTIONS: FilterOption[] = [
  {
    id: 'all_active_members',
    name: 'All active members',
    description: 'Every active member of your gym',
    icon: '✅',
    defaultParams: {}
  },
  {
    id: 'expiring_next_n_days',
    name: 'Membership expiring in next N days',
    description: 'Members whose membership will expire within the specified number of days',
    icon: '⏰',
    defaultParams: { n: 7 },
    paramLabel: 'Days ahead to check'
  },
  {
    id: 'birthday_next_n_days',
    name: 'Members with birthday in next N days',
    description: 'Members whose birthday falls within the next N days',
    icon: '🎂',
    defaultParams: { n: 7 },
    paramLabel: 'Days ahead to check'
  },
  {
    id: 'inactive_members',
    name: 'Not visiting (no check-in in N days)',
    description: 'Members who haven\'t visited the gym in the last N days',
    icon: '😴',
    defaultParams: { n: 30 },
    paramLabel: 'Days since last visit'
  }
];

export default function AudienceSelector({
  tenantId,
  onAudienceSelect,
  onRecipientCount
}: AudienceSelectorProps) {
  const [audienceType, setAudienceType] = useState<'filter' | 'users'>('filter');
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [filterParams, setFilterParams] = useState<Record<string, any>>({});
  const [debouncedParams, setDebouncedParams] = useState<Record<string, any>>({});
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState<string>('');
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);

  const { fetchWithAuth } = useApi();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeFilter = FILTER_OPTIONS.find(f => f.id === selectedFilter);

  // Debounce the N-days input so we don't hit the backend on every keystroke
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedParams(filterParams), 400);
    return () => clearTimeout(timeoutId);
  }, [filterParams]);

  // Fetch users for multi-select
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      return await fetchWithAuth(`/api/members?tenant_id=${tenantId}&limit=1000`);
    },
    enabled: !!tenantId && audienceType === 'users'
  });

  const users: User[] = usersData?.data || [];

  // Recipient count for the selected filter — keyed on the filter and its
  // (debounced) params, so any change automatically refetches fresh numbers.
  const { data: countData, isFetching: isCountLoading } = useQuery({
    queryKey: ['audience-count', tenantId, selectedFilter, debouncedParams],
    queryFn: async () => {
      return await fetchWithAuth('/api/notifications/audience-count', {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: tenantId,
          audience: {
            type: 'filter',
            filter_id: selectedFilter,
            filter_params: debouncedParams
          }
        })
      });
    },
    enabled: !!tenantId && audienceType === 'filter' && !!selectedFilter
  });

  const recipientCount = audienceType === 'users'
    ? selectedUsers.length
    : (countData?.count || 0);
  const excludedCount = audienceType === 'users' ? 0 : (countData?.excluded || 0);

  // Report the current count upward whenever it changes
  useEffect(() => {
    onRecipientCount(recipientCount, excludedCount);
  }, [recipientCount, excludedCount, onRecipientCount]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.phoneNo.includes(userSearch) ||
    (user.email && user.email.toLowerCase().includes(userSearch.toLowerCase()))
  );

  const handleFilterSelect = (filterId: string) => {
    setSelectedFilter(filterId);
    const filter = FILTER_OPTIONS.find(f => f.id === filterId);
    const params = filter?.defaultParams || {};
    setFilterParams(params);
    setDebouncedParams(params);

    onAudienceSelect(filterId ? {
      type: 'filter',
      filter_id: filterId,
      filter_params: params
    } : null);
  };

  const handleFilterParamChange = (key: string, value: any) => {
    const newParams = { ...filterParams, [key]: value };
    setFilterParams(newParams);

    onAudienceSelect({
      type: 'filter',
      filter_id: selectedFilter,
      filter_params: newParams
    });
  };

  const applyUserSelection = (newUsers: User[]) => {
    setSelectedUsers(newUsers);
    onRecipientCount(newUsers.length, 0);
    onAudienceSelect(newUsers.length > 0 ? {
      type: 'users',
      numbers: newUsers.map(u => u.phoneNo)
    } : null);
  };

  const handleUserSelect = (user: User) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    applyUserSelection(
      isSelected ? selectedUsers.filter(u => u.id !== user.id) : [...selectedUsers, user]
    );
  };

  const handleUserRemove = (userId: string) => {
    applyUserSelection(selectedUsers.filter(u => u.id !== userId));
  };

  const handleAudienceTypeChange = (type: 'filter' | 'users') => {
    if (type === audienceType) return;
    setAudienceType(type);

    if (type === 'filter') {
      // Switching to filter mode - clear user selections
      setSelectedUsers([]);
      setUserSearch('');
      setShowUserDropdown(false);

      onAudienceSelect(selectedFilter ? {
        type: 'filter',
        filter_id: selectedFilter,
        filter_params: filterParams
      } : null);
    } else {
      // Switching to users mode - clear filter selections
      setSelectedFilter('');
      setFilterParams({});
      setDebouncedParams({});

      onAudienceSelect(selectedUsers.length > 0 ? {
        type: 'users',
        numbers: selectedUsers.map(u => u.phoneNo)
      } : null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Audience Type Selection */}
      <div className="flex gap-3">
        <button
          onClick={() => handleAudienceTypeChange('filter')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-200 ${
            audienceType === 'filter'
              ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          <Filter className="h-4 w-4" />
          Use Filter
        </button>
        <button
          onClick={() => handleAudienceTypeChange('users')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-200 ${
            audienceType === 'users'
              ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          <Users className="h-4 w-4" />
          Select Users
        </button>
      </div>

      {/* Filter Selection */}
      {audienceType === 'filter' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose Filter Type
            </label>
            <div className="relative">
              <select
                value={selectedFilter}
                onChange={(e) => handleFilterSelect(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">Select a filter...</option>
                {FILTER_OPTIONS.map((filter) => (
                  <option key={filter.id} value={filter.id}>
                    {filter.icon} {filter.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {activeFilter && (
              <p className="text-sm text-gray-600 mt-2">{activeFilter.description}</p>
            )}
          </div>

          {/* Filter Parameters */}
          {activeFilter?.paramLabel && (
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {activeFilter.paramLabel}
              </label>
              <NumberInput
                integer
                min={1}
                max={365}
                emptyValue={activeFilter.defaultParams.n}
                value={filterParams.n ?? activeFilter.defaultParams.n}
                onChange={(v) => handleFilterParamChange('n', v ?? activeFilter.defaultParams.n)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      )}

      {/* User Selection */}
      {audienceType === 'users' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Members
            </label>
            <div className="relative" ref={dropdownRef}>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search members by name, phone, or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onFocus={() => setShowUserDropdown(true)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* User Dropdown */}
              {showUserDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {usersLoading ? (
                    <div className="p-4 text-center text-gray-500">Loading members...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No members found</div>
                  ) : (
                    filteredUsers.map((user) => {
                      const isSelected = selectedUsers.some(u => u.id === user.id);
                      return (
                        <div
                          key={user.id}
                          onClick={() => handleUserSelect(user)}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.phoneNo}</div>
                            {user.email && (
                              <div className="text-xs text-gray-400">{user.email}</div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Selected Members ({selectedUsers.length})</h4>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-sm"
                  >
                    <span>{user.name}</span>
                    <button
                      onClick={() => handleUserRemove(user.id)}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recipient Count */}
      {(isCountLoading || recipientCount > 0 || excludedCount > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900">Recipient Summary</span>
            {isCountLoading && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
          </div>
          <div className="space-y-1 text-sm text-blue-800">
            <div>Total recipients: <span className="font-semibold">{isCountLoading ? '…' : recipientCount}</span></div>
            {excludedCount > 0 && (
              <div>Excluded (opted out): <span className="font-semibold">{excludedCount}</span></div>
            )}
          </div>
        </div>
      )}

      {/* Validation Messages - Only show for filter mode */}
      {audienceType === 'filter' && !isCountLoading && recipientCount === 0 && selectedFilter && countData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center">
              <span className="text-yellow-600 text-xs">!</span>
            </div>
            <span className="text-yellow-800">
              No recipients found for the selected filter criteria. Please adjust your filter parameters.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
